import React, { useEffect, useRef, useState } from 'react';
import videoCallPreview from '../../../../../../assets/images/graphics/video_call_preview.png';

export const CameraSetupCard: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  const [micLevel, setMicLevel] = useState(0);
  const [streamError, setStreamError] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode>();

  /* ── Cleanup on unmount ── */
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      cancelAnimationFrame(animFrameRef.current!);
    };
  }, []);

  const micBars = 12;

  return (
    <div className='w-full rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden text-left'>
      { /* Camera preview */ }
      <div className='bg-black flex flex-col items-center'>
        <div
            className='relative w-full overflow-hidden'
            style={{ aspectRatio: '16/7', maxHeight: 220 }}
        >
          <div className='absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-black'>
            <img
                src={videoCallPreview}
                alt='Professional Video Call Preview'
                className='h-full w-auto opacity-100 object-contain'
            />
            { /* Subtle glow effect */ }
            <div className='absolute inset-0 bg-blue-500/10 mix-blend-overlay pointer-events-none'></div>
          </div>
          { streamError && (
            <div className='absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gray-900/80 backdrop-blur-sm'>
              <svg
                  className='w-12 h-12 text-gray-500'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={1.5}
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z'
                />
              </svg>
              <span className='text-gray-400 text-xs text-center px-4'>
                Camera selection is active,
                <br />
                but preview is hidden for privacy.
              </span>
            </div>
          ) }
        </div>
        <span className='text-gray-400 text-[10px] py-1'>Camera Preview</span>
      </div>

      { /* Device selectors */ }
      <div className='grid grid-cols-2 gap-3 p-4'>
        { /* Camera Device */ }
        <div className='bg-gray-50 rounded-xl border border-gray-100 p-3'>
          <div className='flex items-center gap-2 mb-2'>
            <div className='w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0'>
              <svg
                  className='w-3.5 h-3.5 text-blue-500'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={1.8}
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z'
                />
              </svg>
            </div>
            <div>
              <p className='text-[11px] font-bold text-gray-800 leading-none'>
                Camera Device
              </p>
              <p className='text-[9px] text-gray-400 mt-0.5'>
                Select your preferred webcam
              </p>
            </div>
          </div>
          <select
              value={selectedCamera}
              onChange={(e) => setSelectedCamera(e.target.value)}
              className='w-full text-[11px] border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200'
          >
            { cameras.length === 0 ? (
              <option value=''>Default Camera</option>
            ) : (
              cameras.map((c) => (
                <option key={c.deviceId} value={c.deviceId}>
                  { c.label || 'Camera' }
                </option>
              ))
            ) }
          </select>
        </div>

        { /* Microphone Device */ }
        <div className='bg-gray-50 rounded-xl border border-gray-100 p-3'>
          <div className='flex items-center gap-2 mb-2'>
            <div className='w-7 h-7 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0'>
              <svg
                  className='w-3.5 h-3.5 text-teal-500'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={1.8}
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z'
                />
              </svg>
            </div>
            <div>
              <p className='text-[11px] font-bold text-gray-800 leading-none'>
                Microphone Device
              </p>
              <p className='text-[9px] text-gray-400 mt-0.5'>
                Select your preferred microphone
              </p>
            </div>
          </div>
          <select
              value={selectedMic}
              onChange={(e) => setSelectedMic(e.target.value)}
              className='w-full text-[11px] border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-200'
          >
            { microphones.length === 0 ? (
              <option value=''>Default Mic</option>
            ) : (
              microphones.map((m) => (
                <option key={m.deviceId} value={m.deviceId}>
                  { m.label || 'Microphone' }
                </option>
              ))
            ) }
          </select>

          { /* Mic level */ }
          <div className='mt-2'>
            <p className='text-[9px] text-gray-400 mb-1'>Microphone Level</p>
            <div className='flex items-end gap-0.5 h-3'>
              { Array.from({ length: micBars }).map((_, i) => {
                const threshold = (i / micBars) * 100;
                const active = micLevel > threshold;
                return (
                  <div
                      key={i}
                      className={`flex-1 rounded-sm transition-all duration-75 ${
                        active
                          ? i < micBars * 0.5
                            ? 'bg-blue-500'
                            : i < micBars * 0.8
                              ? 'bg-emerald-400'
                              : 'bg-red-400'
                          : 'bg-gray-200'
                      }`}
                      style={{ height: `${50 + (i / micBars) * 50}%` }}
                  />
                );
              }) }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
